import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicFindings } from '../../lib/api/epics';
import type { FindingView } from '../../lib/api/epics';
import { StatusBadge } from '../ui/StatusBadge';
import type { StatusTone } from '../ui/StatusBadge';

/**
 * S-series — security-scan findings dashboard. Renders the FINDING# rows for a
 * scanned repo: open-finding counts by severity, and the open findings grouped
 * by severity with tool + location. Null-discipline: a repo with no scan (no
 * FINDING# rows) renders nothing.
 */

const SEV_TONE: Record<string, StatusTone> = {
  critical: 'danger', high: 'danger', medium: 'warning', low: 'info', info: 'neutral',
};
const SEV_ORDER: FindingView['severity'][] = ['critical', 'high', 'medium', 'low', 'info'];

export const SecurityFindings: React.FC<{
  epicId: string;
  /**
   * UX-05: the epic detail page's Quality tab shows a badge for the open
   * finding count, but that count only exists once this (self-fetching,
   * lazily-mounted) component has actually loaded its data — there's no
   * open-findings field on the epic itself. Called once per successful
   * fetch so the page can hold the count in state for the tab badge.
   */
  onCount?: (openCount: number) => void;
}> = ({ epicId, onCount }) => {
  const query = useQuery({
    queryKey: ['epic-findings', epicId],
    queryFn: () => fetchEpicFindings(epicId),
    retry: false,
  });

  const data = query.data;

  useEffect(() => {
    if (data) onCount?.(data.summary.open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data || data.summary.total === 0) return null;

  const { summary } = data;
  const open = data.findings.filter((f) => f.status === 'open');
  const bySev = new Map<string, FindingView[]>();
  for (const f of open) {
    if (!bySev.has(f.severity)) bySev.set(f.severity, []);
    bySev.get(f.severity)!.push(f);
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">🔒 Security findings</h3>
        <span className="text-xs text-gray-500">
          {summary.open} open / {summary.total} total
        </span>
      </div>

      {/* Severity rollup chips (open only) */}
      <div className="mb-4 flex flex-wrap gap-2">
        {SEV_ORDER.filter((s) => (summary.by_severity[s] ?? 0) > 0).map((s) => (
          <StatusBadge key={s} tone={SEV_TONE[s]}>{summary.by_severity[s]} {s}</StatusBadge>
        ))}
        {summary.open === 0 && <StatusBadge tone="success">no open findings</StatusBadge>}
      </div>

      {/* Open findings grouped by severity */}
      <div className="space-y-4">
        {SEV_ORDER.filter((s) => bySev.has(s)).map((s) => (
          <div key={s}>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{s}</div>
            <div className="divide-y divide-gray-100">
              {bySev.get(s)!.map((f) => (
                <div key={f.fingerprint} className="flex items-center justify-between gap-2 py-1.5">
                  <div className="min-w-0">
                    <div className="truncate text-xs text-gray-700">{f.message || f.rule || f.cve || 'finding'}</div>
                    {f.file && (
                      <code className="truncate font-mono text-[11px] text-gray-400">
                        {f.file}{f.line != null ? `:${f.line}` : ''}
                      </code>
                    )}
                  </div>
                  <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-500">
                    {f.tool}{f.cve ? ` · ${f.cve}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
